import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  #lockedScrollY = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#handleSubmenuSummaryClick, { capture: true });
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#handleSubmenuSummaryClick, { capture: true });
    this.#clearViewportPositionFix();
    this.#unlockPageScroll();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    if (this.#navigateFromSubmenuTextClick(event)) return;

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    if (details === this.refs.details) {
      this.#lockPageScroll();
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      this.#alignDrawerToViewport();

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Navigate when a user taps the text area of a submenu summary.
   * Tapping the caret keeps the native details toggle behavior.
   * @param {Event | undefined} event
   * @returns {boolean}
   */
  #navigateFromSubmenuTextClick(event) {
    if (event?.defaultPrevented) return false;
    if (!(event instanceof MouseEvent) || event.detail === 0) return false;
    const originalTarget = event.composedPath?.()[0] ?? event.target;

    if (!(originalTarget instanceof Element)) return false;

    const summary = originalTarget.closest('summary[data-menu-drawer-submenu-url]');

    if (!(summary instanceof HTMLElement) || !this.contains(summary)) return false;
    if (this.#isSubmenuToggleClick(summary, event, originalTarget)) return false;

    const url = summary.dataset.menuDrawerSubmenuUrl;

    if (!url || url === '#') return false;

    event.preventDefault();
    window.location.href = url;

    return true;
  }

  /**
   * @param {HTMLElement} summary
   * @param {MouseEvent} event
   * @param {Element} originalTarget
   * @returns {boolean}
   */
  #isSubmenuToggleClick(summary, event, originalTarget) {
    if (originalTarget.closest('[data-menu-drawer-submenu-toggle]')) return true;

    const toggle = summary.querySelector('[data-menu-drawer-submenu-toggle]');

    if (!(toggle instanceof HTMLElement)) return false;

    const rect = toggle.getBoundingClientRect();
    const hitSlop = 12;

    return (
      event.clientX >= rect.left - hitSlop &&
      event.clientX <= rect.right + hitSlop &&
      event.clientY >= rect.top - hitSlop &&
      event.clientY <= rect.bottom + hitSlop
    );
  }

  /**
   * Handles accordion submenu summaries that do not use the declarative open handler.
   * @param {MouseEvent} event
   */
  #handleSubmenuSummaryClick = (event) => {
    this.#navigateFromSubmenuTextClick(event);
  };

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          this.#clearViewportPositionFix();
          this.#unlockPageScroll();

          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  #alignDrawerToViewport() {
    const drawerTop = this.refs.menuDrawer.getBoundingClientRect().top;
    if (Math.abs(drawerTop) <= 1) return;

    const correction = Math.round(-drawerTop);
    this.refs.menuDrawer.style.top = `${correction}px`;

    const backdrop = this.querySelector('.menu-drawer__backdrop');
    if (backdrop instanceof HTMLElement) {
      backdrop.style.top = `${correction}px`;
    }
  }

  #clearViewportPositionFix() {
    this.refs.menuDrawer.style.removeProperty('top');
    const backdrop = this.querySelector('.menu-drawer__backdrop');
    if (backdrop instanceof HTMLElement) {
      backdrop.style.removeProperty('top');
    }
  }

  #lockPageScroll() {
    this.#lockedScrollY = window.scrollY;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#lockedScrollY}px`;
  }

  #unlockPageScroll() {
    if (document.body.style.position !== 'fixed') return;

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#lockedScrollY, behavior: 'instant' });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
